import { bodyOverlaps, mean, spansOverlap } from '../geom'
import { isPlatformSolid, platformPosAt } from '../terrain/platforms'
import { snapToRamps } from '../terrain/ramps'
import { nearBody } from '../track/xindex'
import type { World } from '../world'

/** A trap or rolling projectile that falls and lands like a player (feet-anchored box). */
export interface FallingBody {
  x: number
  y: number
  w: number
  h: number
  vy: number
  grounded: boolean
}

/**
 * Move a falling item one step with the players' terrain rules, after step 3 has moved the
 * platforms to `tick + 1`: ride the platform it rests on, X against solids (a grounded body steps
 * up ≤ `rampSnapPx` like a player), gravity and Y against solids, one-way platforms, then ramps.
 * `vx` is this step's horizontal speed (0 for traps). Returns false when a wall stopped it.
 */
export function moveFallingBody(world: World, b: FallingBody, vx: number): boolean {
  const { cfg } = world
  const prevFoot = b.y
  ridePlatform(world, b)

  let blocked = false
  if (vx !== 0) {
    b.x += vx
    blocked = resolveWalls(world, b, vx)
  }

  const vy0 = b.vy
  b.vy = Math.min(vy0 + cfg.gravityPxPerTick2, cfg.maxFallSpeedPxPerTick)
  b.y += mean(vy0, b.vy)
  const onSolid = landOnSolids(world, b)
  const onPlatform = landOnPlatforms(world, b, prevFoot)
  let landed = onSolid || onPlatform
  if (snapToRamps(world.track, b, cfg.rampSnapPx, landed)) landed = true
  if (landed) b.vy = 0
  b.grounded = landed
  return !blocked
}

/** Push out of solids sideways; returns true when a wall (not a step) was hit. */
function resolveWalls(world: World, b: FallingBody, vx: number): boolean {
  const { solids, index } = world.track
  let blocked = false
  for (const k of nearBody(index.solids, b)) {
    const s = solids[k]!
    if (!bodyOverlaps(b, s)) continue
    if (b.grounded && b.y - s.y <= world.cfg.rampSnapPx) {
      b.y = s.y
    } else {
      b.x = vx > 0 ? s.x - b.w : s.x + s.w
      blocked = true
    }
  }
  return blocked
}

function landOnSolids(world: World, b: FallingBody): boolean {
  const { solids, index } = world.track
  let landed = false
  for (const k of nearBody(index.solids, b)) {
    const s = solids[k]!
    if (!bodyOverlaps(b, s)) continue
    b.y = s.y
    landed = true
  }
  return landed
}

/** One-way landing on a platform top crossed this step (highest top wins). */
function landOnPlatforms(world: World, b: FallingBody, prevFoot: number): boolean {
  const { platforms, index } = world.track
  let best = -1
  let bestTop = 0
  for (const k of nearBody(index.platforms, b)) {
    const pl = platforms[k]!
    const st = world.platformState[k]!
    if (!isPlatformSolid(st, world.tick)) continue
    const prevTop = platformPosAt(pl, world.tick).y
    if (prevFoot > prevTop || b.y < st.y || !spansOverlap(b.x, b.w, st.x, pl.w)) continue
    if (best === -1 || st.y < bestTop) {
      best = k
      bestTop = st.y
    }
  }
  if (best !== -1) b.y = bestTop
  return best !== -1
}

/** A body resting on a moving platform's previous top is carried by its movement this step. */
function ridePlatform(world: World, b: FallingBody): void {
  if (!b.grounded) return
  const { platforms, index } = world.track
  for (const k of nearBody(index.platforms, b)) {
    const pl = platforms[k]!
    const st = world.platformState[k]!
    const prev = platformPosAt(pl, world.tick)
    if (b.y === prev.y && spansOverlap(b.x, b.w, prev.x, pl.w) && isPlatformSolid(st, world.tick)) {
      b.x += st.x - prev.x
      b.y += st.y - prev.y
      return
    }
  }
}
